// PaymentStatus.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { APP_CONFIG } from "../app-config";
import SidebarHeader from "../components/SidebarHeader";
import { encryptDataHybrid, apiCall } from "../utils/api";
import toast, { Toaster } from 'react-hot-toast';
import { IoWarningOutline } from "react-icons/io5";
import { FiArrowLeft } from "react-icons/fi";

const COLORS = APP_CONFIG.colors;

function PaymentStatus() {
  const location = useLocation();
  const navigate = useNavigate();
  const details = location.state;

  if (!details) {
    return <Navigate to="/" replace />;
  }

  const { status, amount, number, order_id, operator_ref, cashback, message } = details;

  // Normalize status to match config keys
  const normalizeStatus = (status) => {
    const statusMap = {
      'Success': 'success',
      'success': 'success',
      'Processed': 'pending',
      'processed': 'pending',
      'Initiated': 'pending',
      'initiated': 'pending',
      'Pending': 'pending',
      'pending': 'pending',
      'Failed': 'failed',
      'failed': 'failed'
    };
    return statusMap[status] || 'pending';
  };

  const normalizedStatus = normalizeStatus(status);
  const isSuccess = normalizedStatus === 'success';
  const isFailed = normalizedStatus === 'failed';
  const isPending = normalizedStatus === 'pending';

  const config = {
    success: {
      color: COLORS.primaryColor,
      title: "Payment Successful!",
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={COLORS.primaryColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ),
      bg: `${COLORS.primaryColor}15`,
      message: "Your recharge has been processed successfully."
    },
    failed: {
      color: COLORS.errorColor,
      title: "Payment Failed",
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={COLORS.errorColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      ),
      bg: `${COLORS.errorColor}15`,
      message: message || "Your payment could not be processed. Please try again."
    },
    pending: {
      color: COLORS.primaryColor,
      title: "Payment Initiated",
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={COLORS.primaryColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
        </svg>
      ),
      bg: `${COLORS.primaryColor}15`,
      message: "Your payment is being processed. You will receive a confirmation shortly."
    }
  };

  const current = config[normalizedStatus];

  return (
    <SidebarHeader>
      <div className="flex items-center gap-3 pb-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-200 hover:bg-gray-300 transition"
        >
          <FiArrowLeft className="text-xl" />
        </button>
        <h1 className="text-lg font-semibold text-gray-800">
          {current.title}
        </h1>
      </div>
      <div className="max-w-7xl mx-auto mb-16">
        {/* Icon Circle */}
        <div
          className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-3"
          style={{ background: current.bg }}
        >
          {current.icon}
        </div>

        {/* Title */}
        <h2
          className="text-xl font-bold mb-1 text-center"
          style={{ color: COLORS.textPrimary }}
        >
          {current.title}
        </h2>

        {/* Message */}
        <p
          className="text-xs mb-6 text-center"
          style={{ color: COLORS.textSecondary }}
        >
          {current.message}
        </p>

        {/* Details Card */}
        <div
          className="rounded-2xl p-5 border mb-4"
          style={{
            background: COLORS.surfaceColor,
            borderColor: COLORS.borderColor,
            borderRadius: COLORS.borderRadius,
          }}
        >
          {order_id && (
            <div className="mb-3 flex justify-between">
              <div className="text-xs" style={{ color: COLORS.textSecondary }}>Order ID</div>
              <div className="text-sm font-semibold text-right" style={{ color: COLORS.textPrimary }}>{order_id}</div>
            </div>
          )}

          {operator_ref && (
            <div className="mb-3 flex justify-between">
              <div className="text-xs" style={{ color: COLORS.textSecondary }}>Operator TXN/RRN</div>
              <div className="text-sm font-semibold text-right" style={{ color: COLORS.textPrimary }}>{operator_ref}</div>
            </div>
          )}

          <div className="mb-3 flex justify-between">
            <div className="text-xs" style={{ color: COLORS.textSecondary }}>Mobile Number</div>
            <div className="text-sm font-semibold text-right" style={{ color: COLORS.textPrimary }}>{number}</div>
          </div>

          <div className="mb-3 flex justify-between">
            <div className="text-xs" style={{ color: COLORS.textSecondary }}>Amount</div>
            <div className="text-sm font-semibold text-right" style={{ color: COLORS.textPrimary }}>₹{amount}</div>
          </div>

          {cashback && cashback > 0 && isSuccess && (
            <div
              className="mt-3 p-2.5 rounded-lg flex justify-between items-center"
              style={{
                background: `${COLORS.primaryColor}10`,
                border: `1px solid ${COLORS.primaryColor}20`
              }}
            >
              <div className="text-xs" style={{ color: COLORS.textSecondary }}>Cashback Earned</div>
              <div className="text-sm font-semibold" style={{ color: COLORS.primaryColor }}>₹{cashback}</div>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={() => navigate("/", { replace: true })}
            className="flex-1 h-12 rounded-lg text-sm font-semibold cursor-pointer transition-all active:scale-98"
            style={{
              background: COLORS.surfaceColor,
              color: COLORS.textPrimary,
              border: `1px solid ${COLORS.borderColor}`,
              borderRadius: COLORS.borderRadius,
            }}
          >
            Go Home
          </button>
          <button
            onClick={() => navigate("/history", { replace: true })}
            className="flex-1 h-12 rounded-lg text-sm font-semibold cursor-pointer transition-all active:scale-98"
            style={{
              background: COLORS.primaryColor,
              color: COLORS.textOnPrimary,
              border: "none",
              borderRadius: COLORS.borderRadius,
            }}
          >
            View History
          </button>
        </div>

        {/* Footer Messages */}
        {isFailed && (
          <p className="mt-6 text-xs text-center" style={{ color: COLORS.textSecondary, lineHeight: 1.5 }}>
            If money was deducted from your wallet, it will be refunded within 24 hours.
          </p>
        )}

        {isPending && (
          <p className="mt-6 text-xs text-center" style={{ color: COLORS.textSecondary, lineHeight: 1.5 }}>
            Your transaction is being processed. Please check back later for updates.
          </p>
        )}

        <Toaster />

        <style>
          {`
          .active\\:scale-98:active {
            transform: scale(0.98);
          }
        `}
        </style>
      </div>
    </SidebarHeader>
  );
}

export default PaymentStatus;